import { useDisclosure } from '@hooks/useDisclosure';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Alert,
  Box,
  Button,
  Divider,
  InputLabel,
  Typography,
} from '@mui/material';

import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import { AutocompleteOption } from '@Components/Form/FormAutoComplete/FormAutoComplete';
import { IntroductionButton, RemarkButton } from '@Components/IconButton';
import Introduction from '@Components/Introduction';
import DeleteModal from '@Components/Modal/DeleteModal';
import Remark from '@Components/Remark';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import CountryDialog from '../../Components/MarketPolicies/CountryDialog';
import {
  DomesticExportSalesRatioFormValues,
  MarketingIndustryFormValues,
} from '../NatureOfBusinessFormProvider/types';

import { disabledFormInputStyles } from '@Components/Form/styles';
import FormatNumberInput from '@Components/FormattedNumberInput/FormatNumberInput';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

type ExportRatioFormFieldsProps = {
  marketPolicyIndex: number;
  isCompletedTh?: boolean;
  isCompletedEn?: boolean;
};

const ExportRatioFormFields = ({
  marketPolicyIndex,
  isCompletedEn,
  isCompletedTh,
}: ExportRatioFormFieldsProps) => {
  const [editIndex, setEditIndex] = useState<number | undefined>();
  const [selectedCountry, setSelectedCountry] = useState<
    AutocompleteOption | undefined
  >();
  const [countriesIndex, setCountriesIndex] = useState<number>(0);
  const { fields, append, remove, update } = useFieldArray({
    name: `marketingIndustry.marketingPolicy.${marketPolicyIndex}.domesticExportSalesRatio`,
  });

  const { setValue, getValues } = useFormContext();

  const shouldShowIntroRef = useRef(false);
  const shouldShowRemarkRef = useRef(false);

  const { salesRatioIntroduction: introduction, salesRatioRemark: remark } =
    getValues(`marketingIndustry.marketingPolicy.${marketPolicyIndex}`) ??
    ({} as MarketingIndustryFormValues);

  shouldShowIntroRef.current = !!introduction;
  shouldShowRemarkRef.current = !!remark;

  const [showIntroduction, setShowIntroduction] = useState<boolean>(
    shouldShowIntroRef.current
  );
  const [showRemark, setShowRemark] = useState<boolean>(
    shouldShowRemarkRef.current
  );

  useEffect(() => {
    const handleMarketingTypeChange = () => {
      setShowIntroduction(false);
      setShowRemark(false);
    };

    window.addEventListener('marketingTypeChanged', handleMarketingTypeChange);

    return () => {
      window.removeEventListener(
        'marketingTypeChanged',
        handleMarketingTypeChange
      );
    };
  }, []);

  const handleOpenIntroduction = () => {
    setShowIntroduction(true);
  };
  const handleOpenRemark = () => {
    setShowRemark(true);
  };

  const handleCloseIntroduction = () => {
    setShowIntroduction(false);
  };
  const handleCloseRemark = () => {
    setShowRemark(false);
  };

  const countriesFormValues =
    useWatch({
      name: `marketingIndustry.marketingPolicy.${marketPolicyIndex}.domesticExportSalesRatio`,
    }) ?? ([] as Array<DomesticExportSalesRatioFormValues>);

  const [dialogOpened, { open: openDialog, close: closeDialog }] =
    useDisclosure();
  const [openDelete, { open: openDeleteDialog, close: closeDeleteDialog }] =
    useDisclosure();

  const domesticRatioFormName = `marketingIndustry.marketingPolicy.${marketPolicyIndex}.domesticExportSalesRatio`;

  const handleCountrySubmit = (
    selected: AutocompleteOption,
    index?: number
  ) => {
    if (typeof index === 'number') {
      setValue(`${domesticRatioFormName}.${index}.ratio`, null);
      update(index, {
        sequence: index + 1,
        countryNameTh: selected.label,
        countryNameEn: selected.value as string,
        ratio: null,
      });
    } else {
      append({
        sequence: fields.length + 1,
        countryNameTh: selected.label,
        countryNameEn: selected.value as string,
        ratio: null,
      });
    }
    closeDialog();
    setEditIndex(undefined);
    setSelectedCountry(undefined);
  };

  const handleEdit = (index: number) => {
    const country = countriesFormValues[index];
    setSelectedCountry({
      label: country.countryNameTh,
      value: country.countryNameEn,
    });
    setEditIndex(index);
    openDialog();
  };

  const handleDeleteCountry = (index: number) => {
    setCountriesIndex(index);
    openDeleteDialog();
  };

  const existingCountries = countriesFormValues.map(
    (country: { countryNameTh: string; countryNameEn: string }) => ({
      label: country.countryNameTh,
      value: country.countryNameEn,
    })
  );

  const disableActions = !!isCompletedTh && !!isCompletedEn;

  return (
    <Box mt={2}>
      <Flex alignItems={'center'} gap={1}>
        <Typography fontWeight={800}>
          สัดส่วนการจำหน่าย ในประเทศต่อการส่งออกไปจำหน่ายต่างประเทศ (ถ้ามี)
        </Typography>
        <Flex gap={0.5} alignItems={'center'}>
          <IntroductionButton
            onClick={() => handleOpenIntroduction()}
            disabled={disableActions}
          />
          <RemarkButton
            onClick={() => handleOpenRemark()}
            disabled={disableActions}
          />
        </Flex>
      </Flex>

      {showIntroduction && (
        <Box mt={2}>
          <Introduction
            onDelete={handleCloseIntroduction}
            topic="สัดส่วนการจำหน่าย ในประเทศต่อการส่งออกไปจำหน่ายต่างประเทศ"
            setOpen={setShowIntroduction}
            fieldPath={`marketingIndustry.marketingPolicy.${marketPolicyIndex}.salesRatioIntroduction`}
            isCompletedEn={isCompletedEn}
            isCompletedTh={isCompletedTh}
          />
        </Box>
      )}

      <Alert severity={'info'} sx={{ mt: 2 }}>
        คำชี้แจง :
        ให้ระบุสัดส่วนการจำหน่ายในประเทศต่อการส่งออกไปจำหน่ายต่างประเทศชื่อประเทศหรือกลุ่มประเทศที่จำหน่ายผลิตภัณฑ์หรือบริการ
      </Alert>

      {fields.map((field, index) => (
        <Flex key={field.id} alignItems={'center'} gap={3} mt={2} width={1}>
          {countriesFormValues[index]?.countryNameTh && (
            <>
              <Box width={1 / 3}>
                <InputLabel sx={{ textAlign: 'right' }}>
                  {countriesFormValues[index]?.countryNameTh}
                </InputLabel>
                <Flex justifyContent={'flex-end'}>
                  <Typography>(%)</Typography>
                  <EditOutlinedIcon
                    fontSize="small"
                    sx={{
                      cursor:
                        !!isCompletedTh || !!isCompletedEn
                          ? 'not-allowed'
                          : 'pointer',
                      color:
                        !!isCompletedTh || !!isCompletedEn
                          ? 'grey.300'
                          : 'inherit',
                    }}
                    onClick={() => {
                      if (!isCompletedTh && !isCompletedEn) handleEdit(index);
                    }}
                  />
                  <DeleteOutlineOutlinedIcon
                    fontSize="small"
                    sx={{
                      cursor:
                        !!isCompletedTh || !!isCompletedEn
                          ? 'not-allowed'
                          : 'pointer',
                      color:
                        !!isCompletedTh || !!isCompletedEn
                          ? 'grey.300'
                          : 'inherit',
                    }}
                    onClick={() => {
                      if (!isCompletedTh && !isCompletedEn) {
                        handleDeleteCountry(index);
                      }
                    }}
                  />
                </Flex>
              </Box>
              <FormInput
                sx={{
                  ...(disableActions ? disabledFormInputStyles : {}),
                  '& input': {
                    textAlign: 'right',
                  },
                }}
                placeholder="กรุณาระบุจำนวน"
                name={`${domesticRatioFormName}.${index}.ratio`}
                slotProps={{
                  htmlInput: { min: 0 },
                  input: {
                    inputComponent: FormatNumberInput as React.ComponentType,
                    inputProps: {
                      useThousandSeparator: false,
                      allowNegative: false,
                      max: 100,
                    },
                  },
                }}
              />
              <Box width={1 / 3} />
            </>
          )}
        </Flex>
      ))}

      <Flex width={1} alignItems={'center'} gap={1} my={3}>
        <Divider sx={{ flex: 1 }} />
        <Button
          color="inherit"
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={!disableActions ? openDialog : undefined}
          disabled={disableActions}
        >
          เพิ่มรายการ
        </Button>
        <Divider sx={{ flex: 1 }} />
      </Flex>

      {showRemark && (
        <Remark
          onDelete={handleCloseRemark}
          topic="สัดส่วนการจำหน่าย ในประเทศต่อการส่งออกไปจำหน่ายต่างประเทศ"
          setOpen={setShowRemark}
          fieldPath={`marketingIndustry.marketingPolicy.${marketPolicyIndex}.salesRatioRemark`}
          isCompletedEn={isCompletedEn}
          isCompletedTh={isCompletedTh}
        />
      )}

      <CountryDialog
        open={dialogOpened}
        onClose={closeDialog}
        onCountrySubmit={handleCountrySubmit}
        existingCountries={existingCountries}
        isEdit={editIndex !== undefined}
        editIndex={editIndex}
        selectedCountry={selectedCountry}
      />
      <DeleteModal
        opened={openDelete}
        close={closeDeleteDialog}
        confirm={() => {
          remove(countriesIndex);
          closeDeleteDialog();
        }}
        topic={countriesFormValues[countriesIndex]?.countryNameTh}
      />
    </Box>
  );
};

export default ExportRatioFormFields;
